import { useState } from 'react';
import { Button, SideDrawer } from '@/components';
import { PencilIcon, TrashIcon } from '../../components/Icons';
import { useQuotationProduct } from '../hooks';
import {
  formatQuoteMoney,
  // formatQuoteDateTime,
  resolveQuotationMediaUrl,
} from '../utils';
import { parseApiError } from '@/common/utils';
import { ClickableProductImage } from '@/components/ClickableProductImage';
import { ImagePreviewModal } from '@/components/ImagePreviewModal';
import { QuotationActivityTimeline } from './QuotationActivityTimeline';

function DetailRow({ label, value, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-xs text-[#868686] shrink-0">{label}</span>
      <span className="text-xs font-semibold text-[#1F2241] text-right break-words min-w-0">
        {children ||
          (value === undefined || value === null || value === '' ? '—' : value)}
      </span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="bg-[#F5F5F5] rounded-[16px] p-4 flex flex-col gap-2.5">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#50AB3D] pb-0.5 border-b border-[#EBF4EA]">
        {title}
      </h4>
      <div className="bg-white rounded-[12px] p-3.5 flex flex-col gap-2">
        {children}
      </div>
    </div>
  );
}

/**
 * QuotationProductDetailModal - Dedicated read-only slide-over drawer for
 * viewing full WhatsApp quotation product details, specifications, and metadata.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether drawer is open
 * @param {() => void} props.onClose - Close callback
 * @param {string|number} props.companyId - Company ID
 * @param {string|number} props.quotationId - Quotation ID
 * @param {Object|null} [props.product] - Shallow product object from table
 * @param {string|number|null} [props.productId] - Product ID
 * @param {string} [props.currency='INR'] - Currency code
 * @param {boolean} [props.canEdit=true] - Edit permission
 * @param {(product: Object) => void} [props.onEdit] - Switch to edit drawer
 * @param {(productId: string|number) => void} [props.onDelete] - Trigger delete confirmation
 */
export function QuotationProductDetailModal({
  isOpen,
  onClose,
  companyId,
  quotationId,
  product = null,
  productId = null,
  currency = 'INR',
  canEdit = true,
  onEdit,
  onDelete,
}) {
  const [previewImage, setPreviewImage] = useState(null);
  const targetProductId = product?.id || productId;

  const {
    data: fetchedProduct,
    isLoading: isFetchingProduct,
    isError: isProductError,
    error: productFetchError,
    refetch: refetchProduct,
  } = useQuotationProduct(
    { companyId, quotationId, productId: targetProductId },
    { enabled: isOpen && Boolean(targetProductId) },
  );

  const activeProduct = fetchedProduct || product;

  const imageUrl = resolveQuotationMediaUrl(
    activeProduct?.effective_image_url ||
      activeProduct?.image_url ||
      activeProduct?.image ||
      '',
  );

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        width="md"
        className="q-drawer sm:w-[60vw] sm:max-w-[560px] lg:w-[42vw] lg:max-w-[580px]"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-text-primary truncate">
                Product Details
              </h2>
              <p className="text-xs text-text-muted mt-0.5 truncate">
                {activeProduct?.name || 'Quotation Catalog Item'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canEdit && onEdit && activeProduct && (
                <button
                  type="button"
                  aria-label="Edit product"
                  onClick={() => {
                    onClose();
                    onEdit(activeProduct);
                  }}
                  className="w-8 h-8 rounded-[8px] bg-[#EBF4EA] text-[#50AB3D] flex items-center justify-center hover:bg-[#d8edd6] transition-colors focus:outline-none"
                  title="Edit Product"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {canEdit && onDelete && activeProduct && (
                <button
                  type="button"
                  aria-label="Delete product"
                  onClick={() => {
                    onClose();
                    onDelete(activeProduct.id);
                  }}
                  className="w-8 h-8 rounded-[8px] bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors focus:outline-none"
                  title="Delete Product"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        }
        footer={
          <div className="flex items-center justify-between gap-3 w-full">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="h-10 px-4 text-sm"
            >
              Close
            </Button>
            {canEdit && onEdit && activeProduct && (
              <Button
                type="button"
                variant="primary"
                icon={<PencilIcon className="w-3.5 h-3.5" />}
                iconPosition="start"
                onClick={() => {
                  onClose();
                  onEdit(activeProduct);
                }}
                className="h-10 px-5 text-sm"
              >
                Edit Product
              </Button>
            )}
          </div>
        }
      >
        {isProductError && !activeProduct ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center font-bold text-lg">
              !
            </div>
            <p className="text-sm font-semibold text-[#1F2241]">
              Failed to load product details
            </p>
            <p className="text-xs text-[#868686] max-w-sm">
              {parseApiError(productFetchError).message ||
                'Unable to retrieve product information from server.'}
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => refetchProduct()}
              className="mt-2 text-xs h-8 px-4"
            >
              Retry
            </Button>
          </div>
        ) : isFetchingProduct && !activeProduct ? (
          <div className="flex flex-col gap-6 py-4 animate-pulse">
            <div className="flex items-center gap-4 bg-[#F5F5F5] rounded-[16px] p-4">
              <div className="w-[72px] h-[72px] rounded-[12px] bg-white shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="h-5 w-36 bg-white rounded" />
                <div className="h-4 w-24 bg-white rounded" />
              </div>
            </div>
            <div className="h-36 bg-[#F5F5F5] rounded-[16px]" />
            <div className="h-28 bg-[#F5F5F5] rounded-[16px]" />
          </div>
        ) : activeProduct ? (
          <div className="flex flex-col gap-5 py-1">
            {/* ── Hero Card: Thumbnail + Name + Price + Status ── */}
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 flex items-center gap-4">
              <ClickableProductImage
                imageUrl={imageUrl}
                productName={activeProduct.name}
                className="!w-[76px] !h-[76px] !min-w-[76px] !min-h-[76px] rounded-[12px] shadow-sm bg-white"
                onClick={
                  imageUrl
                    ? () =>
                        setPreviewImage({
                          url: imageUrl,
                          name: activeProduct.name,
                        })
                    : undefined
                }
              />
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-[#1F2241] truncate">
                    {activeProduct.name}
                  </h3>
                  {/* {activeProduct.is_available === false ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                      Unavailable
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Available in Catalog
                    </span>
                  )} */}
                </div>

                <p className="text-lg font-bold text-[#50AB3D]">
                  {formatQuoteMoney(activeProduct.price, currency)}
                </p>

                {activeProduct.sku && (
                  <p className="text-xs text-[#868686]">
                    SKU:{' '}
                    <span className="font-medium text-[#1F2241]">
                      {activeProduct.sku}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* ── Section: Pricing & Inventory ── */}
            <DetailSection title="Pricing & Inventory">
              <DetailRow
                label="Unit Price"
                value={formatQuoteMoney(activeProduct.price, currency)}
              />
              <DetailRow
                label="Tax Rate"
                value={
                  activeProduct.tax_rate != null &&
                  activeProduct.tax_rate !== ''
                    ? `${activeProduct.tax_rate}%`
                    : '0%'
                }
              />
              <DetailRow
                label="Min Order Qty (MOQ)"
                value={activeProduct.moq || 1}
              />
              {activeProduct.sku && (
                <DetailRow label="Product SKU" value={activeProduct.sku} />
              )}
              <DetailRow
                label="Catalog Order Sequence"
                value={
                  activeProduct.order != null
                    ? `# ${activeProduct.order}`
                    : 'Auto'
                }
              />
              <DetailRow label="Availability">
                {activeProduct.is_available === false ? (
                  <span className="text-xs font-semibold text-gray-500">
                    Hidden from WhatsApp Catalog
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-emerald-600">
                    Active & Available
                  </span>
                )}
              </DetailRow>
            </DetailSection>

            {/* ── Section: Description / Specifications ── */}
            <DetailSection title="Description & Specifications">
              {activeProduct.description ? (
                <p className="text-xs sm:text-sm text-[#1F2241] leading-relaxed whitespace-pre-line break-words">
                  {activeProduct.description}
                </p>
              ) : (
                <p className="text-xs text-[#868686] italic">
                  No detailed description provided for this product.
                </p>
              )}
            </DetailSection>

            <QuotationActivityTimeline
              companyId={companyId}
              quotationId={quotationId}
              productId={targetProductId}
              title="History"
              compact
              scrollClassName="max-h-[280px]"
            />
          </div>
        ) : null}
      </SideDrawer>

      <ImagePreviewModal
        isOpen={!!previewImage}
        imageUrl={previewImage?.url}
        title={previewImage?.name}
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
}

export default QuotationProductDetailModal;
